import {FetchBaseQueryError, FetchBaseQueryMeta} from "@reduxjs/toolkit/query";

import {isRtkqError} from "../../store/utils/is_rtkq_error";
import {RATE_LIMIT_FALLBACK_S} from "../constants/reservation_constants";
import {RESERVATION_ERROR_MESSAGES} from "../constants/reservation_copy";
import {IReservationError, ReservationErrorKind, ReservationPhase} from "../types/IReservationError";
import {getApiErrorCode, getFirstString, isDisplayableApiMessage, isString} from "./api_error_text";
import {isRecord} from "./is_record";

// status of an error that never reached the base query (e.g. an aborted request)
export const SERIALIZED_ERROR_STATUS = "SERIALIZED_ERROR";

// a 403 carries no code: CSRF failures come from Django itself, the others from DRF in the API's locale
const CSRF_PATTERN = /csrf/i;
const UNAUTHENTICATED_PATTERN = /credentials|not provided|authentication|uwierzytelni/i;
const NOT_VERIFIED_PATTERN = /verif|weryfik/i;

// DRF field errors read for the message, after the general texts
const FIELD_ERROR_KEYS = ["code", "reservation_handle", "property"] as const;

// Business errors come as a 409 with a machine-readable `code` and a user-facing `detail`.
// A Map, so that a code such as "constructor" cannot resolve to an Object.prototype member.
const API_ERROR_CODE_KINDS = new Map<string, ReservationErrorKind>([
    ["name_required", "profile_incomplete"],
    // reported as a rejection - only users with a verified phone can start a reservation, so a missing one is an inconsistency
    ["phone_required", "rejected"],
    ["already_reserved", "taken"],
    ["attempts_exceeded", "user_limit"],
    ["not_reservable", "not_reservable"],
    ["handle_expired", "expired"],
    ["invalid_code", "invalid_code"],
    ["rate_limited", "rate_limited"],
    ["service_unavailable", "server"],
    // legacy, still mapped while the API may send it
    ["account_not_verified", "not_verified"]
]);

// the API text replaces the frontend copy only for these kinds
const API_MESSAGE_KINDS: ReservationErrorKind[] = [
    "taken",
    "user_limit",
    "not_reservable",
    "not_verified",
    "profile_incomplete",
    "rejected",
    "validation",
    "forbidden"
];
// an incomplete profile is retried once the name form has saved the missing data
const RETRYABLE_KINDS: ReservationErrorKind[] = ["rate_limited", "profile_incomplete", "validation", "network", "server", "unknown"];
const RESENDABLE_KINDS: ReservationErrorKind[] = ["invalid_code", "expired", "validation", "network", "server", "unknown"];

// the API texts in the order: error, detail, non_field_errors, field errors
const readApiTexts = (data: unknown): string[] => {
    if (!isRecord(data)) {
        const text = Array.isArray(data) ? getFirstString(data) : null;

        return text === null ? [] : [text];
    }

    // a string `code` is the machine code, not a field error
    const fieldKeys = FIELD_ERROR_KEYS.filter((key) => !(key === "code" && isString(data.code)));

    return [data.error, data.detail, data.non_field_errors, ...fieldKeys.map((key) => data[key])].map(getFirstString).filter(isString);
};

const hasFieldError = (data: unknown, field: string) => isRecord(data) && getFirstString(data[field]) !== null;

const getForbiddenKind = (text: string): ReservationErrorKind => {
    if (CSRF_PATTERN.test(text)) {
        return "csrf";
    }

    if (UNAUTHENTICATED_PATTERN.test(text)) {
        return "unauthenticated";
    }

    if (NOT_VERIFIED_PATTERN.test(text)) {
        return "not_verified";
    }

    return "forbidden";
};

// for responses without a code the map knows
const getHttpErrorKind = (status: number, phase: ReservationPhase, data: unknown, text: string): ReservationErrorKind => {
    if (status === 400 || status === 422) {
        if (phase === "initiate") {
            return "validation";
        }

        // a missing or unknown handle can only be replaced by a new code
        return hasFieldError(data, "reservation_handle") ? "expired" : "invalid_code";
    }

    if (status === 401) {
        return "unauthenticated";
    }

    if (status === 403) {
        return getForbiddenKind(text);
    }

    if (status === 404 || status === 410) {
        return phase === "confirm" ? "expired" : "unknown";
    }

    // an unknown or missing code is no proof that the property is taken - marking it taken would flip the CTA to reserved
    if (status === 409) {
        return "rejected";
    }

    if (status === 429) {
        return "rate_limited";
    }

    return status >= 500 ? "server" : "unknown";
};

const getErrorKind = (response: FetchBaseQueryError, phase: ReservationPhase, text: string): ReservationErrorKind => {
    if (typeof response.status === "number") {
        const apiErrorCode = getApiErrorCode(response.data);
        const apiErrorCodeKind = apiErrorCode === null ? undefined : API_ERROR_CODE_KINDS.get(apiErrorCode);

        return apiErrorCodeKind ?? getHttpErrorKind(response.status, phase, response.data, text);
    }

    if (response.status === "PARSING_ERROR") {
        // a non-JSON body never comes from the API itself - it is an ingress or upstream page, so only the CSRF hint is read from it
        if (response.originalStatus === 403) {
            return typeof response.data === "string" && CSRF_PATTERN.test(response.data) ? "csrf" : "forbidden";
        }

        return response.originalStatus === 429 ? "rate_limited" : "server";
    }

    if (response.status === "FETCH_ERROR" || response.status === "TIMEOUT_ERROR") {
        return "network";
    }

    return "unknown";
};

const parseRetryAfter = (value: string | null | undefined): number | null => {
    if (!value) {
        return null;
    }

    const seconds = Number(value);

    if (Number.isFinite(seconds) && seconds >= 0) {
        return Math.ceil(seconds);
    }

    const date = Date.parse(value);

    return Number.isNaN(date) ? null : Math.max(0, Math.ceil((date - Date.now()) / 1000));
};

const createReservationError = (params: {
    kind: ReservationErrorKind;
    phase: ReservationPhase;
    raw: IReservationError["raw"];
    apiMessage: string | null;
    retryAfterSeconds?: number;
}): IReservationError => {
    const {kind, phase, raw, apiMessage, retryAfterSeconds} = params;

    return {
        kind,
        phase,
        message: API_MESSAGE_KINDS.includes(kind) && isDisplayableApiMessage(apiMessage) ? apiMessage.trim() : RESERVATION_ERROR_MESSAGES[kind],
        retryable: RETRYABLE_KINDS.includes(kind),
        resendAllowed: RESENDABLE_KINDS.includes(kind),
        ...(retryAfterSeconds === undefined ? {} : {retryAfterSeconds}),
        raw
    };
};

export const normalizeReservationError = (response: FetchBaseQueryError, phase: ReservationPhase, meta?: FetchBaseQueryMeta): IReservationError => {
    const texts = readApiTexts(response.data);
    const kind = getErrorKind(response, phase, texts.join(" "));
    const params = {kind, phase, raw: {status: response.status, data: response.data}, apiMessage: texts[0] ?? null};

    if (kind === "rate_limited") {
        // the API's own `rate_limited` comes without Retry-After, so the fallback lock applies
        return createReservationError({...params, retryAfterSeconds: parseRetryAfter(meta?.response?.headers.get("Retry-After")) ?? RATE_LIMIT_FALLBACK_S});
    }

    return createReservationError(params);
};

const isReservationError = (value: unknown): value is IReservationError =>
    isRecord(value) && typeof value.kind === "string" && typeof value.phase === "string" && typeof value.message === "string" && isRecord(value.raw);

// for errors caught from `unwrap()` - an already normalized error is returned as is, anything without a response is a network failure
export const toReservationError = (error: unknown, phase: ReservationPhase): IReservationError => {
    if (isRecord(error) && isReservationError(error.data)) {
        return error.data;
    }

    if (isRtkqError(error)) {
        return normalizeReservationError(error, phase);
    }

    return createReservationError({kind: "network", phase, raw: {status: SERIALIZED_ERROR_STATUS, data: error}, apiMessage: null});
};
import {FetchBaseQueryError, FetchBaseQueryMeta} from "@reduxjs/toolkit/query";

import {isRtkqError} from "../../store/utils/is_rtkq_error";
import {RATE_LIMIT_FALLBACK_S} from "../constants/reservation_constants";
import {RESERVATION_ERROR_MESSAGES} from "../constants/reservation_copy";
import {IReservationError, ReservationErrorKind, ReservationPhase} from "../types/IReservationError";
import {getApiErrorCode, getFirstString, isDisplayableApiMessage, isString} from "./api_error_text";
import {isRecord} from "./is_record";

// status of an error that never reached the base query (e.g. an aborted request)
export const SERIALIZED_ERROR_STATUS = "SERIALIZED_ERROR";

// a 403 carries no code: CSRF failures come from Django itself, the others from DRF in the API's locale
const CSRF_PATTERN = /csrf/i;
const UNAUTHENTICATED_PATTERN = /credentials|not provided|authentication|uwierzytelni/i;
const NOT_VERIFIED_PATTERN = /verif|weryfik/i;

// DRF field errors read for the message, after the general texts
const FIELD_ERROR_KEYS = ["code", "reservation_handle", "property"] as const;

// Business errors come as a 409 with a machine-readable `code` and a user-facing `detail`.
// A Map, so that a code such as "constructor" cannot resolve to an Object.prototype member.
const API_ERROR_CODE_KINDS = new Map<string, ReservationErrorKind>([
    ["name_required", "profile_incomplete"],
    // reported as a rejection - only users with a verified phone can start a reservation, so a missing one is an inconsistency
    ["phone_required", "rejected"],
    ["already_reserved", "taken"],
    ["attempts_exceeded", "user_limit"],
    ["not_reservable", "not_reservable"],
    ["handle_expired", "expired"],
    ["invalid_code", "invalid_code"],
    ["rate_limited", "rate_limited"],
    ["service_unavailable", "server"],
    // legacy, still mapped while the API may send it
    ["account_not_verified", "not_verified"]
]);

// the API text replaces the frontend copy only for these kinds
const API_MESSAGE_KINDS: ReservationErrorKind[] = [
    "taken",
    "user_limit",
    "not_reservable",
    "not_verified",
    "profile_incomplete",
    "rejected",
    "validation",
    "forbidden"
];
// an incomplete profile is retried once the name form has saved the missing data
const RETRYABLE_KINDS: ReservationErrorKind[] = ["rate_limited", "profile_incomplete", "validation", "network", "server", "unknown"];
const RESENDABLE_KINDS: ReservationErrorKind[] = ["invalid_code", "expired", "validation", "network", "server", "unknown"];

// the API texts in the order: error, detail, non_field_errors, field errors
const readApiTexts = (data: unknown): string[] => {
    if (!isRecord(data)) {
        const text = Array.isArray(data) ? getFirstString(data) : null;

        return text === null ? [] : [text];
    }

    // a string `code` is the machine code, not a field error
    const fieldKeys = FIELD_ERROR_KEYS.filter((key) => !(key === "code" && isString(data.code)));

    return [data.error, data.detail, data.non_field_errors, ...fieldKeys.map((key) => data[key])].map(getFirstString).filter(isString);
};

const hasFieldError = (data: unknown, field: string) => isRecord(data) && getFirstString(data[field]) !== null;

const getForbiddenKind = (text: string): ReservationErrorKind => {
    if (CSRF_PATTERN.test(text)) {
        return "csrf";
    }

    if (UNAUTHENTICATED_PATTERN.test(text)) {
        return "unauthenticated";
    }

    if (NOT_VERIFIED_PATTERN.test(text)) {
        return "not_verified";
    }

    return "forbidden";
};

// for responses without a code the map knows
const getHttpErrorKind = (status: number, phase: ReservationPhase, data: unknown, text: string): ReservationErrorKind => {
    if (status === 400 || status === 422) {
        if (phase === "initiate") {
            return "validation";
        }

        // a missing or unknown handle can only be replaced by a new code
        return hasFieldError(data, "reservation_handle") ? "expired" : "invalid_code";
    }

    if (status === 401) {
        return "unauthenticated";
    }

    if (status === 403) {
        return getForbiddenKind(text);
    }

    if (status === 404 || status === 410) {
        return phase === "confirm" ? "expired" : "unknown";
    }

    // an unknown or missing code is no proof that the property is taken - marking it taken would flip the CTA to reserved
    if (status === 409) {
        return "rejected";
    }

    if (status === 429) {
        return "rate_limited";
    }

    return status >= 500 ? "server" : "unknown";
};

const getErrorKind = (response: FetchBaseQueryError, phase: ReservationPhase, text: string): ReservationErrorKind => {
    if (typeof response.status === "number") {
        const apiErrorCode = getApiErrorCode(response.data);
        const apiErrorCodeKind = apiErrorCode === null ? undefined : API_ERROR_CODE_KINDS.get(apiErrorCode);

        return apiErrorCodeKind ?? getHttpErrorKind(response.status, phase, response.data, text);
    }

    if (response.status === "PARSING_ERROR") {
        // a non-JSON body never comes from the API itself - it is an ingress or upstream page, so only the CSRF hint is read from it
        if (response.originalStatus === 403) {
            return typeof response.data === "string" && CSRF_PATTERN.test(response.data) ? "csrf" : "forbidden";
        }

        return response.originalStatus === 429 ? "rate_limited" : "server";
    }

    if (response.status === "FETCH_ERROR" || response.status === "TIMEOUT_ERROR") {
        return "network";
    }

    return "unknown";
};

const parseRetryAfter = (value: string | null | undefined): number | null => {
    if (!value) {
        return null;
    }

    const seconds = Number(value);

    if (Number.isFinite(seconds) && seconds >= 0) {
        return Math.ceil(seconds);
    }

    const date = Date.parse(value);

    return Number.isNaN(date) ? null : Math.max(0, Math.ceil((date - Date.now()) / 1000));
};

const createReservationError = (params: {
    kind: ReservationErrorKind;
    phase: ReservationPhase;
    raw: IReservationError["raw"];
    apiMessage: string | null;
    retryAfterSeconds?: number;
}): IReservationError => {
    const {kind, phase, raw, apiMessage, retryAfterSeconds} = params;

    return {
        kind,
        phase,
        message: API_MESSAGE_KINDS.includes(kind) && isDisplayableApiMessage(apiMessage) ? apiMessage.trim() : RESERVATION_ERROR_MESSAGES[kind],
        retryable: RETRYABLE_KINDS.includes(kind),
        resendAllowed: RESENDABLE_KINDS.includes(kind),
        ...(retryAfterSeconds === undefined ? {} : {retryAfterSeconds}),
        raw
    };
};

export const normalizeReservationError = (response: FetchBaseQueryError, phase: ReservationPhase, meta?: FetchBaseQueryMeta): IReservationError => {
    const texts = readApiTexts(response.data);
    const kind = getErrorKind(response, phase, texts.join(" "));
    const params = {kind, phase, raw: {status: response.status, data: response.data}, apiMessage: texts[0] ?? null};

    if (kind === "rate_limited") {
        // the API's own `rate_limited` comes without Retry-After, so the fallback lock applies
        return createReservationError({...params, retryAfterSeconds: parseRetryAfter(meta?.response?.headers.get("Retry-After")) ?? RATE_LIMIT_FALLBACK_S});
    }

    return createReservationError(params);
};

const isReservationError = (value: unknown): value is IReservationError =>
    isRecord(value) && typeof value.kind === "string" && typeof value.phase === "string" && typeof value.message === "string" && isRecord(value.raw);

// for errors caught from `unwrap()` - an already normalized error is returned as is, anything without a response is a network failure
export const toReservationError = (error: unknown, phase: ReservationPhase): IReservationError => {
    if (isRecord(error) && isReservationError(error.data)) {
        return error.data;
    }

    if (isRtkqError(error)) {
        return normalizeReservationError(error, phase);
    }

    return createReservationError({kind: "network", phase, raw: {status: SERIALIZED_ERROR_STATUS, data: error}, apiMessage: null});
};
